import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { Layout } from "@/components/Layout";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Donate from "@/pages/Donate";
import DonationSuccess from "@/pages/DonationSuccess";
import Pricing from "@/pages/Pricing";
import Transparency from "@/pages/Transparency";
import Trending from "@/pages/Trending";
import CheckDetail from "@/pages/CheckDetail";
import FactOfDay from "@/pages/FactOfDay";
import DonorWall from "@/pages/DonorWall";
import About from "@/pages/About";
import Themes from "@/pages/Themes";
import Shop from "@/pages/Shop";
import ShopComingSoon from "@/pages/ShopComingSoon";
import MerchSuccess from "@/pages/MerchSuccess";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Developers from "@/pages/Developers";
import ApiDocs from "@/pages/ApiDocs";
import CheckPage from "@/pages/CheckPage";
import AdminOrders from "@/pages/AdminOrders";
import { ThemeProvider } from "@/lib/theme";
import { OnboardingModal } from "@/components/OnboardingModal";
import { trackPageView } from "@/lib/analytics";
import { loadAdSense } from "@/lib/adsense";

function AppRouter() {
  const location = useLocation();

  // One-time: load Google AdSense loader if configured
  useEffect(() => {
    loadAdSense();
  }, []);

  // Fire GA4 page_view on every route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Emergent OAuth returns to /dashboard#session_id=... — catch it *before* normal routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/signup" element={<AuthPage mode="signup" />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/donate/success" element={<DonationSuccess />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/transparency" element={<Transparency />} />
        <Route path="/trending" element={<Trending />} />
        <Route path="/today" element={<FactOfDay />} />
        <Route path="/wall" element={<DonorWall />} />
        <Route path="/about" element={<About />} />
        <Route path="/themes" element={<Themes />} />
        {/* Storefront hidden — /shop shows Coming Soon. /shop/internal-preview is the secret preview. */}
        <Route path="/shop" element={<ShopComingSoon />} />
        <Route path="/shop/internal-preview" element={<Shop />} />
        <Route path="/merch/success" element={<MerchSuccess />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/developers" element={<Developers />} />
        <Route path="/docs/api" element={<ApiDocs />} />
        <Route path="/check/:checkId" element={<CheckPage />} />
        <Route path="/check/:id" element={<CheckDetail />} />
        <Route path="/admin" element={<AdminOrders />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <AppRouter />
              <OnboardingModal />
              <Toaster
                position="top-right"
                theme="dark"
                toastOptions={{
                  style: {
                    background: "hsl(var(--card))",
                    color: "hsl(var(--foreground))",
                    border: "1px solid hsl(var(--border))",
                  },
                }}
              />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
